import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Cards from 'src/components/Cards';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button, { isButtonEnabled } from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import PopUp from 'src/components/design-system/PopUp';
import useBoolean from 'src/hooks/useBoolean';
import { InvoiceLineItem } from 'src/services/useInvoiceLineItemsApi';
import { round } from 'src/utils';
import { useContextSelector } from 'use-context-selector';
import CombineLineItemsWizard from './CombineLineItemsWizard';
import { InvoiceContext } from './InvoiceDetailsContext';

export const InformationComponent: FC<{ content: string }> = ({ content }) => (
  <Cards title="Information" styleType="information">
    <ul style={{ textAlign: 'left' }}>{content}</ul>
  </Cards>
);

const CombineLineItems = () => {
  const throttledInvoiceLineItems = useContextSelector(
    InvoiceContext,
    (value) => value.throttledInvoiceLineItems
  );
  const isInvoiceInProgress = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceInProgress
  );
  const setInvoiceLineItems = useContextSelector(
    InvoiceContext,
    (value) => value.setInvoiceLineItems
  );

  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  const selectedLineItemIds = useContextSelector(
    InvoiceContext,
    (value) => value.selectedLineItemIds
  );
  const setSelectedLineItemIds = useContextSelector(
    InvoiceContext,
    (value) => value.setSelectedLineItemIds
  );
  const errors = useContextSelector(InvoiceContext, (value) => value.errors);

  const {
    value: showCombineLineItems,
    setTrue: openDialog,
    setFalse: closeDialog,
  } = useBoolean(false);
  const [data, setData] = useState<InvoiceLineItem[]>([] as InvoiceLineItem[]);

  const selectedLineItems = useMemo(
    () =>
      throttledInvoiceLineItems?.filter((lineItem) =>
        selectedLineItemIds.includes(lineItem?.receiptLineItemId)
      ),
    [throttledInvoiceLineItems, selectedLineItemIds]
  );
  const isItemReq = errors?.lineItems?.map((el) => el?.productItemNum.flat()).flat();

  const {
    combinableLineItems,
    messages,
    itemWithLeastItemNumber,
    isDisabled,
    isPOQtyOfItemWithLeastItemNumZero,
  } = useMemo(() => {
    let messages: string[] = [];

    const lineItemCount = selectedLineItems.length;
    const isTwoLineItemsSelected = lineItemCount === 2;

    const combinableLineItems: InvoiceLineItem[] =
      selectedLineItems.map((row) => ({
        ...row,
        selected: false,
      })) || [];

    const leastItemNumber = Math.min(
      Number(combinableLineItems[0]?.poLineItemNumber),
      Number(combinableLineItems[1]?.poLineItemNumber)
    );

    const biggerItemNumber = Math.max(
      Number(combinableLineItems[0]?.poLineItemNumber),
      Number(combinableLineItems[1]?.poLineItemNumber)
    );

    const itemWithBiggerItemNumber: InvoiceLineItem = combinableLineItems.find(
      (item) => item.poLineItemNumber === biggerItemNumber
    ) as InvoiceLineItem;

    const itemWithLeastItemNumber: InvoiceLineItem = combinableLineItems.find(
      (item) => item.poLineItemNumber === leastItemNumber
    ) as InvoiceLineItem;

    const notAtLeastOneZeroPOQty = !selectedLineItems?.filter(
      (lineItemObj) => lineItemObj?.poQty === 0
    )?.length;
    const isPOQtyOfItemWithLeastItemNumZero = itemWithLeastItemNumber?.poQty === 0;
    if (isInvoiceEditDisabled) {
      messages = ['Current document is not editable'];
    } else if (!isInvoiceInProgress) {
      messages = ['Current document is not in progress'];
    } else if (!isTwoLineItemsSelected) {
      messages = ['Please select exactly 2 Items to Combine.'];
    } else if (
      selectedLineItems[0]?.isNewlyAddedLineItemNow ||
      selectedLineItems[1]?.isNewlyAddedLineItemNow
    ) {
      messages = ['Please save the newly added Line Item on the document'];
    } else if (notAtLeastOneZeroPOQty) {
      messages = ['At least one of the selected Items should have PO Quantity of 0.'];
    } else if (!isButtonEnabled(itemWithBiggerItemNumber?.canDelete)) {
      messages = ['Item with bigger line item number is not deletable'];
    } else {
      messages = [];
    }

    const isDisabled =
      !isInvoiceInProgress ||
      !isTwoLineItemsSelected ||
      notAtLeastOneZeroPOQty ||
      !isButtonEnabled(itemWithBiggerItemNumber?.canDelete) ||
      !!selectedLineItems[0]?.isNewlyAddedLineItemNow ||
      !!selectedLineItems[1]?.isNewlyAddedLineItemNow ||
      isItemReq?.length > 0 ||
      isInvoiceEditDisabled;
    return {
      combinableLineItems,
      messages,
      itemWithLeastItemNumber,
      isPOQtyOfItemWithLeastItemNumZero,
      isDisabled,
    };
  }, [isInvoiceEditDisabled, isInvoiceInProgress, isItemReq.length, selectedLineItems]);

  useEffect(() => {
    if (showCombineLineItems) {
      const newQuantity = round(
        Number(combinableLineItems[0]?.quantityReceived) +
          Number(combinableLineItems[1]?.quantityReceived),
        2
      );
      setData([
        {
          ...itemWithLeastItemNumber,
          quantityReceived: newQuantity,
          subTotal: newQuantity * itemWithLeastItemNumber.priceAmt,
        },
      ]);
    }
  }, [combinableLineItems, itemWithLeastItemNumber, showCombineLineItems]);

  const handleClose = useCallback(
    (success: boolean) => () => {
      if (success) {
        setInvoiceLineItems((prev) => [
          ...prev.filter((i) => !selectedLineItemIds.includes(i.receiptLineItemId)),
          ...data,
        ]);
        setSelectedLineItemIds([]);
      }
      closeDialog();
    },
    [closeDialog, setInvoiceLineItems, data, selectedLineItemIds, setSelectedLineItemIds]
  );

  return (
    <>
      <PopUp
        content={
          <div className="m-1 max-w-[300px]">
            <PopUpContentWrapper type="Information" contents={messages} />
            <PopUpContentWrapper type="Error" contents={isItemReq} />
          </div>
        }
        show={!!messages.length || isItemReq?.length > 0}
      >
        <Button onClick={openDialog} disabled={isDisabled}>
          Combine Line Items
        </Button>
      </PopUp>

      <Dialog
        width={700}
        title="Combine Invoice Items"
        open={showCombineLineItems}
        onClose={closeDialog}
      >
        <CombineLineItemsWizard
          combinableLineItems={combinableLineItems}
          close={handleClose}
          itemWithLeastItemNumber={itemWithLeastItemNumber as InvoiceLineItem}
          data={data}
          setData={setData}
          isPOQtyOfItemWithLeastItemNumZero={isPOQtyOfItemWithLeastItemNumZero}
        />
      </Dialog>
    </>
  );
};

export default CombineLineItems;
